import { IHasFrameContext } from "UI/UI/UI/Controls/ControlInterfaces";
import { PopupMenu2, PopupMenuOptions } from "UI/UI/UI/PopupMenu2";
import { DataConverter } from "../../Global/Base/Converters/DataConverter";
import { Code } from "../../Global/Base/Utils/EventUtils";
import { createElement, registerCustomElement } from "../UI.ElementarControls/Utils/CustomElementUtils";
import { FrameContext } from "../UI/UI/UIManagerInterfaces";
import { Control } from "./Control";
import { EditableControl } from "./EditableControl";

export class Edit extends EditableControl implements IHasFrameContext {
    protected override _innerElement: HTMLInputElement;
    protected override _multiline: boolean;
    protected _textAreaElement: HTMLTextAreaElement;
    protected _valuePopup: PopupMenu2;

    private _frameContext: FrameContext | undefined = undefined;

    //#region Properties

    //TODO 33753 použít mixin
    public get frameContext(): FrameContext | undefined {
        return this._frameContext;
    }

    public set frameContext(value: FrameContext | undefined) {
        this._frameContext = value;
    }

    public override set value(val: any) {
        this.controlValue = val;

        if (this._textAreaElement) {
            this._textAreaElement.value = val;
        }
    }

    public override get value(): any {
        return this.controlValue;
    }

    public set maxLength(val: number) {
        if (val && val > 0) {
            this._innerElement.maxLength = val;
        }
    }

    public get maxLength(): number {
        return this._innerElement.maxLength;
    }

    public get placeholder(): string {
        return this._innerElement.placeholder;
    }

    public set placeholder(value: string) {
        this._innerElement.placeholder = value;
    }

    public override get multiline(): boolean {
        return this._multiline;
    }

    public override set multiline(value: boolean) {
        this._multiline = value;

        if (value) {
            this._textAreaElement = createElement({ type: "textarea" }) as HTMLTextAreaElement;
            this._textAreaElement.addEventListener("input", () => this.controlValue = this._textAreaElement.value);
            this._textAreaElement.addEventListener("change", () => this.callEvent("change"));
            this._textAreaElement.addEventListener("keydown", (e) => {
                if (e.code === Code.Tab) {
                    this._valuePopup?.close();
                }
            });
            this._textAreaElement.classList.add("Multiline");
        }
    }

    //#region controlValue
    public get controlValue(): string {
        return this._innerElement.value;
    }

    /**
     * Nastavi hodnotu editu
     */
    public set controlValue(value: string) {
        const el = this._innerElement;

        if (this.maxLength > 0 && value && value.length > this.maxLength) {
            el.value = value.substr(0, this.maxLength);
            //this.alertOnLength();
        } else {
            el.value = value;
        }
        this.validate();
    }

    protected static override get observedAttributes(): string[] {
        return ["maxlength", "ispassword", "placeholder"].concat(EditableControl.observedAttributes);
    }
    //#endregion controlValue
    //#endregion Properties

    public override focus(): void {
        this._innerElement.focus();
    }

    protected override _constructor(): void {
        super._constructor();

        const tcont: string = this.textContent.trim();
        this.innerHTML = "";

        this._innerElement = createElement({ type: "input" }) as HTMLInputElement;
        this._innerElement.setAttribute("autocomplete", "off");
        this.appendChild(this._innerElement);
        this.classList.add("Edit");

        if (tcont.length > 0) {
            this.value = tcont;
        }
    }

    protected override connectedCallback(): void {
        super.connectedCallback();

        if (this._innerElement) {
            Control.registerEventListener(this._eventGroup, this._innerElement, "change", (e) => {
                e.stopPropagation();
                this.callEvent("change");
            });
        }
    }

    protected override _focusInHandler(e: Event): void {
        super._focusInHandler(e);
        if (this.multiline) {
            this._showValuePopup(e);
        }
    }

    protected override _focusOutHandler(): void {
        if (this.multiline && !this._textAreaElement.focus) {
            this._valuePopup.close();
        }
        super._focusOutHandler();
    }

    protected _showValuePopup(e: Event): void {
        if (!this._valuePopup) {
            const wrapper = createElement({ classes: "MultilineWrapper" });
            wrapper.appendChild(this._textAreaElement);
            this._valuePopup = new PopupMenu2({
                target: this._innerElement as HTMLElement,
                content: wrapper,
                showPosition: "overlay",
                width: "content",
                closeButton: true,
                targetShowsPopupOnFocus: true,
            } as PopupMenuOptions);
        }
        this._textAreaElement.style.height = "";
        this._textAreaElement.style.width = "";
        this._textAreaElement.value = this._innerElement.value;
        this._valuePopup.show(e);
        this._textAreaElement.focus();
    }

    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        switch (attrName) {
            case "maxlength":
                this.maxLength = +newVal;
                break;
            case "ispassword":
                if (DataConverter.toBoolean(newVal, false)) {
                    this._innerElement.type = "password";
                } else {
                    this._innerElement.type = "text";
                }
                break;
            case "placeholder":
                this.placeholder = newVal;
                break;
        }
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();
        if (this._valuePopup) {
            this._valuePopup.dispose();
            this._valuePopup = null;
        }
    }
}

registerCustomElement("tsc-edit", Edit);
